import React, {createContext, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {AppState, InteractionManager} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import { io as createSocketClient } from 'socket.io-client';
import {getAccessToken} from './storage';
import {safeSetItem} from './storageQuota';
import {getSetting} from './persistentSettings';
import ErrorReportingService from './ErrorReportingService';
import Observables from '@happy-gastro/global-observables';
import {redact, subscribeDemoMode} from '../demo';
import HappyAlert from '../components/HappyAlert';
import {slimHistoryEntry} from './orderHistorySlim';
import i18n from '../../i18n';
import {
  applyPairingOverlay,
  applyStatusOverlay,
  buildOrderPatch,
  cameToForeground,
  collectRecentlyTouchedIds,
  collectTouchedSince,
  hasDisplayableOrderShape,
  isSlimRealtimeNotice,
  orderBelongsToPlace,
  PAYMENT_CLOSE_OVERLAY_TTL_MS,
  pruneStaleCachedOrders,
  reconcileOrderLists,
  selectConfirmedGhostIds,
  selectPaidTableOrderIds,
  shouldAllowSync,
  shouldSkipPollAfterRecentFetch,
  trackServerMisses,
} from './orderSyncReliability';

/**
 * Optimized Orders Context with reactive updates and batched state management
 * Prevents stale data issues and ensures UI updates instantly
 */
export const OrdersContext = createContext();

const OrdersProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [tableOrders, setTableOrders] = useState({}); // Maps tableId to its orders
  const [isConnected, setIsConnected] = useState(true);
  const [activeTransport, setActiveTransport] = useState('polling');
  const syncIntervalRef = useRef(null);
  const mqttClientRef = useRef(null);
  const mqttConnectedRef = useRef(false);
  const socketClientRef = useRef(null);
  const socketConnectedRef = useRef(false);
  const networkOnlineRef = useRef(true);
  // Concurrency guard for fetchOrdersFromAPI. We DELIBERATELY use a ref (not
  // React state) as the real lock — the context exposes it via a getter: a long-lived polling interval
  // closes over a stale state value, and — far worse — if the lock were ever
  // left engaged (a hung request, an InteractionManager that never settled) the
  // app would silently stop fetching until a restart. The ref is released
  // synchronously in `finally`, and `syncStartedAtRef` powers a watchdog that
  // force-clears a lock older than SYNC_WATCHDOG_MS so we can never get stuck.
  const isSyncingRef = useRef(false);
  const syncStartedAtRef = useRef(0);
  const appStateRef = useRef(AppState.currentState || 'active');
  // Realtime transport orchestration / anti-flap state.
  const realtimeLockedRef = useRef(false);        // pinned to polling, realtime attempts suppressed
  const realtimeConfirmedRef = useRef(false);     // realtime link survived the stability window
  const realtimeRetryTimerRef = useRef(null);     // single upgrade retry scheduled after the cooldown
  const realtimeStabilityTimerRef = useRef(null); // stability confirmation timer
  const startRealtimeAttemptRef = useRef(null);
  const lockRealtimeToPollingRef = useRef(null);
  const connectSocketCandidateRef = useRef(null);
  const handleRealtimeConnectedRef = useRef(null);
  const handleRealtimeLostRef = useRef(null);
  const realtimeDedupeRef = useRef(new Map());
  const ordersRef = useRef([]);
  // Id of the restaurant place this terminal is currently operating. The backend
  // realtime channel is scoped per restaurant (not per place), so events for a
  // sibling place arrive here too; this ref lets us filter them out so another
  // place's order can never land in this terminal's lists / pending dialog.
  // Kept fresh on mount and at the top of every fetch (where the place is read).
  const currentPlaceIdRef = useRef(null);
  // Content signature of the orders currently committed to React state. Lets
  // commitOrders() skip a state update when a poll/realtime event produced data
  // identical to what we already hold (see commitOrders for why this matters).
  const ordersSignatureRef = useRef(null);
  const fetchOrdersFromAPIRef = useRef(null);
  const lastSuccessfulFetchAtRef = useRef(0);
  // A fetch requested while another is in flight is QUEUED (not dropped) and
  // runs right after it, so a change announced mid-request is never missed
  // until the next poll. Holds the merged options of the queued run, or null.
  const queuedFetchOptionsRef = useRef(null);
  // Debounce for reloads triggered by slim realtime notices (bursts collapse).
  const realtimeRefetchTimerRef = useRef(null);
  // order id -> epoch ms of the last local create/edit/realtime upsert. Shields
  // fresh orders from the ghost purge and keeps them from being overwritten by
  // a server snapshot that was requested before the change.
  const localTouchedAtRef = useRef(new Map());
  // order id -> consecutive server responses that omitted this live local order
  // (see trackServerMisses); lets the routine poll purge confirmed ghosts.
  const serverMissCountsRef = useRef(new Map());
  const batchUpdatesRef = useRef(null);
  const pendingUpdatesRef = useRef({}); // Batch pending updates
  // Optimistic food-pairing overlay. When the operator pairs an online-order
  // line (Foodora/Wolt/Webshop) to a local product we apply the pairing to the
  // order IN MEMORY immediately and persist it to the backend in the background,
  // so the UI never blocks on the PUT round-trip. Each entry is re-applied on top
  // of every committed list (see applyPendingPairings/commitOrders) until the
  // server echoes the pairing back — otherwise the 10s poll's server-authoritative
  // reconcile would wipe the not-yet-persisted pairing and the row would flash
  // back to "Nincs párosítva". Keyed `${orderId}:${itemId}` -> { orderId, itemId, food }.
  const pendingPairingsRef = useRef(new Map());
  // Optimistic order-status overlay. When the operator accepts/advances an order
  // we flip its status in memory immediately so it moves lists at once; a
  // server-authoritative poll must not snap it back to `pending` before the
  // backend echoes the change. Keyed `orderId` -> { orderId, status, savedAt }.
  const pendingStatusRef = useRef(new Map());
  const API_URL = 'https://api.happysolutions.hu/api';
  const POLLING_FALLBACK_MS = 10 * 1000;
  // Hard ceiling on a single order-list request. Mobile networks routinely
  // drop a socket without ever resolving/rejecting fetch(); without this the
  // sync lock (and the user's view of orders) would hang indefinitely.
  const FETCH_TIMEOUT_MS = 15 * 1000;
  // If a sync has somehow been "in progress" longer than this, treat the lock
  // as stale and allow a fresh attempt. Defence-in-depth against a stuck lock.
  const SYNC_WATCHDOG_MS = 30 * 1000;
  const REALTIME_DEDUPE_TTL_MS = 2 * 60 * 1000;
  const REALTIME_REFETCH_DEBOUNCE_MS = 300;
  // Anti-flap realtime policy: once MQTT/Socket.IO is unavailable we pin to
  // polling and STAY there for a full cooldown before a SINGLE upgrade retry.
  // A freshly opened realtime link must also survive the stability window
  // before we trust it (and flip the badge), so a connect→drop flap can never
  // make the indicator blink between "Polling" and "Socket.IO".
  const REALTIME_RETRY_COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes pinned to polling
  const REALTIME_STABILITY_MS = 8 * 1000;            // link must hold this long to be trusted
  const SOCKET_CONNECT_TIMEOUT_MS = 8 * 1000;
  const SOCKET_IO_CANDIDATES = useMemo(() => {
    // On an HTTPS page the browser blocks insecure ws:// (mixed content), which
    // otherwise kills the realtime upgrade and pins us to polling. Upgrade the
    // scheme to match a secure origin so Socket.IO can stay connected.
    const securePage = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
    const secureUpgrade = (url) => {
      if (!securePage) return url;
      if (url.startsWith('ws://')) return 'wss://' + url.slice('ws://'.length);
      if (url.startsWith('http://')) return 'https://' + url.slice('http://'.length);
      return url;
    };
    return [
      'https://api.happysolutions.hu',
      'https://sync.happysolutions.hu',
      // Legacy direct port, kept until every node serves the new sync host.
      secureUpgrade('ws://sync.happygastro.org:1993'),
    ];
  }, []);

  const emitApiToLocalStatus = useCallback((payload = {}) => {
    try {
      Observables.call('offlineSync:status', {
        channel: 'apiToLocal',
        timestamp: new Date().toISOString(),
        ...payload,
      });
    } catch (_) {
      // Best-effort status reporting only.
    }
  }, []);

  const classifyApiToLocalError = useCallback((error) => {
    const message = String(error?.message || error || 'Unknown API sync error');
    const lowered = message.toLowerCase();
    const httpStatus = Number(error?.httpStatus);

    if (error?.name === 'AbortError' || lowered.includes('timeout') || lowered.includes('aborted')) {
      return { reasonType: 'timeout', reason: 'Order list request timed out', httpStatus: null };
    }

    if (httpStatus === 401 || httpStatus === 403) {
      return { reasonType: 'auth', reason: message, httpStatus };
    }

    if (Number.isFinite(httpStatus)) {
      return { reasonType: 'api', reason: message, httpStatus };
    }

    if (lowered.includes('network') || lowered.includes('failed to fetch') || lowered.includes('internet')) {
      return { reasonType: 'network', reason: 'Network error while fetching orders', httpStatus: null };
    }

    return { reasonType: 'unknown', reason: message, httpStatus: null };
  }, []);

  // Filter order properties to reduce memory footprint
  const filterOrderProperties = useCallback((order) => {
    return {
      _id: order._id,
      status: order.status,
      storno: order.storno,
      orderCode: order.orderCode,
      serviceFee: order.serviceFee,
      deliveryFee: order.deliveryFee,
      serviceFeePrice: order.serviceFeePrice,
      discountAmount: order.discountAmount,
      discountPrice: order.discountPrice,
      orderType: order.orderType,
      deliveryType: order.deliveryType,
      orderSum: order.orderSum,
      originalSum: order.originalSum,
      productsPrice: order.productsPrice,
      discounts: order.discounts,
      // Risk/fraud assessment needs these (see app/helpers/orderRisk.js)
      orderRisk: order.orderRisk,
      invoiceData: order.invoiceData,
      needInvoice: order.needInvoice,
      isPayed: order.isPayed,
      tipAmount: order.tipAmount,
      items: Array.isArray(order.items)
        ? order.items.filter(Boolean).map((item) => ({
            _id: item._id,
            name: item.name,
            remoteName: item.remoteName,
            remoteCode: item.remoteCode,
            price: item.price,
            remotePrice: item.remotePrice,
            amount: item.amount,
            discountAmount: item.discountAmount,
            item: item.item,
            extras: Array.isArray(item.extras)
              ? item.extras.filter(Boolean).map((extra) => ({
                  _id: extra._id,
                  item: extra.item,
                  name: extra.name,
                  price: extra.price,
                  remoteName: extra.remoteName,
                  remoteCode: extra.remoteCode,
                  remotePrice: extra.price,
                }))
              : [],
            round: item.round,
            comment: item.comment,
            comments: item.comments,
            size: item.size,
            sizeIndex: item.sizeIndex,
          }))
        : [],
      paymentType: order.paymentType,
      tableId: order.tableId,
      tableName: order.tableName,
      createdAt: order.createdAt,
      preOrder: order.preOrder,
      userInfo: order.userInfo,
      addressInfo: order.addressInfo,
      delivery: order.delivery,
      printed: order.printed,
      restaurantPlace: order.restaurantPlace,
      orderFrom: order.orderFrom,
      paymentTypes: order.paymentTypes,
      token: order.token,
      remoteId: order.remoteId,
      origin: order.origin,
      platformId: order.platformId,
      venueId: order.venueId,
      VIPCard: order.VIPCard,
      proforma: order.proforma,
      history: Array.isArray(order.history)
        ? order.history.filter(
            (historyItem) =>
              historyItem &&
              (historyItem.event === 'removeItems' || historyItem.event === 'payment')
          ).map(slimHistoryEntry)
        : [],
      events: Array.isArray(order.events)
        ? order.events.filter(Boolean).map((event) => ({
            time: event.time,
            event: event.event,
            details: event.details,
            user: event.user,
          }))
        : [],
    };
  }, []);

  // Build table-to-orders map for faster lookups
  const buildTableOrdersMap = useCallback((ordersList) => {
    const map = {};
    ordersList.forEach((order) => {
      if (order.tableId) {
        if (!map[order.tableId]) {
          map[order.tableId] = [];
        }
        map[order.tableId].push(order);
      }
    });
    return map;
  }, []);

  // Re-apply any optimistic pairings the backend has not yet echoed, and prune
  // the ones it now has (server becomes authoritative). Ref-only side effect;
  // stable across renders. Returns the input untouched when nothing is pending.
  const applyPendingPairings = useCallback((list) => {
    if (!pendingPairingsRef.current.size) {
      return Array.isArray(list) ? list : [];
    }
    const { orders: overlaid, confirmedKeys } = applyPairingOverlay(list, pendingPairingsRef.current);
    confirmedKeys.forEach((key) => pendingPairingsRef.current.delete(key));

    return overlaid;
  }, []);

  // Re-apply any optimistic order-status changes the backend has not yet echoed,
  // and prune the ones it now reflects (server becomes authoritative). Same
  // ref-only, stable side-effect contract as applyPendingPairings above.
  const applyPendingStatuses = useCallback((list) => {
    if (!pendingStatusRef.current.size) {
      return Array.isArray(list) ? list : [];
    }
    const { orders: overlaid, confirmedKeys } = applyStatusOverlay(list, pendingStatusRef.current);
    confirmedKeys.forEach((key) => pendingStatusRef.current.delete(key));

    return overlaid;
  }, []);

  // Single funnel for every write into the `orders` state. It serializes the
  // next list once and compares it to the last committed signature; if the data
  // is byte-for-byte identical it KEEPS THE EXISTING ARRAY REFERENCE and skips
  // setOrders/setTableOrders entirely.
  //
  // This is the core fix for the polling-induced slowness. The "never lose an
  // order" reconciliation runs on a 10s floor (plus every realtime event), and
  // each run handed React a brand-new array even when nothing had changed. A new
  // reference flows through the context value into every consumer, and several
  // screens recompute StatisticsHelper (an O(n·items) pass) on every `orders`
  // change — so an idle restaurant still paid a full app-wide re-render every
  // 10 seconds. Gating on the signature makes an unchanged poll genuinely free
  // while a real change still propagates instantly.
  //
  // `ordersRef.current` holds the UN-overlaid BASE (exactly what the server /
  // reconcile produced); the optimistic pairing overlay is applied only to the
  // React-visible state and the signature. Keeping the base overlay-free is what
  // makes a failed background pairing revert INSTANTLY (and offline): dropping the
  // pending entry and re-committing the same base yields an un-paired view again,
  // instead of a "paired" value baked permanently into the base.
  const commitOrders = useCallback(
    (nextOrders, { persist = true } = {}) => {
      const base = Array.isArray(nextOrders) ? nextOrders : [];
      // Overlay not-yet-persisted pairings AND status changes so a
      // server-authoritative poll can never wipe an edit the operator just made
      // (see pendingPairingsRef / pendingStatusRef).
      const view = applyPendingStatuses(applyPendingPairings(base));
      const signature = JSON.stringify(view);

      if (ordersSignatureRef.current === signature) {
        return false;
      }

      ordersSignatureRef.current = signature;
      ordersRef.current = base;
      setOrders(view);
      setTableOrders(buildTableOrdersMap(view));

      if (persist) {
        // Reuse the signature we just computed instead of stringifying twice.
        // Quota-guarded: the serialized order list is one of the largest keys in
        // the app, and on a full backend this would otherwise reject (an
        // unhandled QuotaExceededError on web) instead of evicting and retrying.
        safeSetItem('orders', signature, { label: 'orders', skipIfUnchanged: false })
            .catch(() => {});
      }

      return true;
    },
    [applyPendingPairings, applyPendingStatuses, buildTableOrdersMap]
  );

  // Batch update mechanism - reduces re-renders
  const applyBatchedUpdates = useCallback(() => {
    if (Object.keys(pendingUpdatesRef.current).length === 0) return;

    const updatedOrders = [...(ordersRef.current || [])];
    const updates = pendingUpdatesRef.current;
    pendingUpdatesRef.current = {};

    Object.entries(updates).forEach(([orderId, changedFields]) => {
      const index = updatedOrders.findIndex((o) => o && o._id === orderId);
      if (index !== -1) {
        updatedOrders[index] = { ...updatedOrders[index], ...changedFields };
      }
    });

    commitOrders(updatedOrders, { persist: false });
  }, [commitOrders]);

  // Queue an order update without immediately triggering a render
  const queueOrderUpdate = useCallback((orderId, updates) => {
    pendingUpdatesRef.current[orderId] = {
      ...pendingUpdatesRef.current[orderId],
      ...updates,
    };

    // Clear previous timeout
    if (batchUpdatesRef.current) {
      globalThis.clearTimeout(batchUpdatesRef.current);
    }

    // Apply batched updates on next tick
    batchUpdatesRef.current = globalThis.setTimeout(applyBatchedUpdates, 16); // ~60fps
  }, [applyBatchedUpdates]);

  const isRealtimeDuplicate = useCallback((eventKey) => {
    if (!eventKey) return false;

    const now = Date.now();
    const dedupeMap = realtimeDedupeRef.current;
    const existing = dedupeMap.get(eventKey);

    if (existing && now - existing < REALTIME_DEDUPE_TTL_MS) {
      return true;
    }

    dedupeMap.set(eventKey, now);
    for (const [key, seenAt] of dedupeMap.entries()) {
      if (now - seenAt > REALTIME_DEDUPE_TTL_MS) {
        dedupeMap.delete(key);
      }
    }

    return false;
  }, [REALTIME_DEDUPE_TTL_MS]);

  const normalizeRealtimeOrder = useCallback((payload = {}) => {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    // Realtime orders arrive over Socket.IO / MQTT, which bypass global.fetch —
    // so demo-mode redaction must be applied explicitly here (HTTP-fetched orders
    // are already redacted by app/demo/demoHttpProxy). No-op when demo is off.
    if (payload.order && typeof payload.order === 'object') {
      return redact(payload.order);
    }

    if (payload.payload && typeof payload.payload === 'object') {
      return redact(payload.payload);
    }

    return redact(payload);
  }, []);

  const upsertOrderInMemory = useCallback((incomingOrder) => {
    if (!incomingOrder || !incomingOrder._id) {
      return false;
    }

    // Reject orders belonging to a different restaurant place. The realtime
    // channel is restaurant-wide, so without this a sibling place's order would
    // be inserted into this terminal's list and surface in the pending-orders
    // dialog / delivery screens. Fail-open: only drops a place we KNOW differs.
    if (!orderBelongsToPlace(incomingOrder, currentPlaceIdRef.current)) {
      return false;
    }

    const normalizedOrder = filterOrderProperties(incomingOrder);
    localTouchedAtRef.current.set(normalizedOrder._id, Date.now());
    const previousOrders = ordersRef.current || [];
    const existingIndex = previousOrders.findIndex((order) => order && order._id === normalizedOrder._id);
    const nextOrders = [...previousOrders];

    if (existingIndex === -1) {
      nextOrders.unshift(normalizedOrder);
    } else {
      // Non-destructive merge: a realtime event often carries only the fields it
      // changed. Patch ONLY those (see buildOrderPatch) so a partial payload can
      // never blank good data like `restaurantPlace`/`orderType`/`items` — which
      // would drop the order out of the place filter and make it "disappear".
      const patch = buildOrderPatch(incomingOrder, normalizedOrder);
      nextOrders[existingIndex] = {
        ...nextOrders[existingIndex],
        ...patch,
      };
    }

    commitOrders(nextOrders);
    return true;
  }, [commitOrders, filterOrderProperties]);

  // Drop a stale optimistic order that the backend de-duplicated into a
  // DIFFERENT canonical order. The offline-first create flow inserts an
  // optimistic order under the id the device minted (see
  // paymentHelper.send_storeOrder / SyncManager). When the backend resolves that
  // id to an ALREADY-open table order (server-side dineIn dedup) it persists
  // under a different canonical `_id` and returns it. Without this the device
  // would keep showing its now-orphaned optimistic order forever — a ghost
  // "pending" order on an already-occupied table (and the table would look busy
  // after a payment that actually closed the real order). SyncManager calls this
  // on sync success whenever the server's canonical id differs from the local
  // one. The authoritative order arrives via the follow-up reload / realtime
  // event, so we only need to remove the orphan here.
  const removeOptimisticOrder = useCallback((staleId) => {
    const normalizedStaleId = staleId ? String(staleId) : '';
    if (!normalizedStaleId) {
      return false;
    }

    const previousOrders = ordersRef.current || [];
    const nextOrders = previousOrders.filter((order) => String(order?._id) !== normalizedStaleId);

    if (nextOrders.length === previousOrders.length) {
      return false;
    }

    return commitOrders(nextOrders);
  }, [commitOrders]);

  const stopPollingFallback = useCallback(() => {
    if (syncIntervalRef.current) {
      globalThis.clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
  }, []);

  const startPollingFallback = useCallback(() => {
    if (syncIntervalRef.current) {
      return;
    }

    syncIntervalRef.current = globalThis.setInterval(() => {
      if (!networkOnlineRef.current) {
        return;
      }
      // A realtime event / screen focus refetched moments ago: this poll would
      // download and diff the exact same list again. Skipping it still bounds
      // staleness by the poll interval (the next tick fetches).
      if (shouldSkipPollAfterRecentFetch(lastSuccessfulFetchAtRef.current, Date.now(), POLLING_FALLBACK_MS)) {
        return;
      }
      // Call directly — fetchOrdersFromAPI is its own non-blocking async task
      // and carries its own concurrency guard + timeout. We intentionally do
      // NOT wrap in InteractionManager here: if interactions never settle (a
      // stuck handle, a long animation) the poll would silently never fire.
      fetchOrdersFromAPIRef.current?.();
    }, POLLING_FALLBACK_MS);
  }, [POLLING_FALLBACK_MS]);

  /**
   * Single source of truth for the transport badge. Realtime is only ever shown
   * once it has been CONFIRMED (survived the stability window) and a transport
   * is actually connected; otherwise we display polling. While the realtime
   * lock is engaged we force polling regardless of any stray socket/MQTT state,
   * which is what stops the badge from blinking.
   */
  const applyTransportState = useCallback(() => {
    const realtimeUp = realtimeConfirmedRef.current
      && !realtimeLockedRef.current
      && (mqttConnectedRef.current || socketConnectedRef.current);

    // The transport badge reflects realtime once it is confirmed, but the 10s
    // polling floor ALWAYS keeps running. Realtime is an accelerator, never a
    // replacement: a socket that connects but then silently stops delivering
    // order:* events (wrong room, backend hiccup, proxy idle-timeout) must not
    // be able to strand the operator on stale orders. We used to call
    // stopPollingFallback() here the instant a socket confirmed, which meant a
    // connected-but-silent socket left incoming pending orders invisible until
    // something else forced a fetch — e.g. navigating to another screen and
    // back (its focus refresh refetched, and react-freeze re-rendered the
    // online screen on return). Keeping the floor on makes every screen
    // self-heal within one poll interval no matter what realtime does.
    setActiveTransport(realtimeUp ? (mqttConnectedRef.current ? 'mqtt' : 'socket.io') : 'polling');
    startPollingFallback();
  }, [startPollingFallback]);

  const clearRealtimeStabilityTimer = useCallback(() => {
    if (realtimeStabilityTimerRef.current) {
      globalThis.clearTimeout(realtimeStabilityTimerRef.current);
      realtimeStabilityTimerRef.current = null;
    }
  }, []);

  /**
   * Fully tear down every realtime transport (Socket.IO + MQTT) and reset the
   * confirmation/stability state. Listeners are removed BEFORE disconnecting so
   * the teardown itself cannot re-enter the connect/disconnect handlers.
   */
  const teardownRealtimeTransports = useCallback(() => {
    clearRealtimeStabilityTimer();
    realtimeConfirmedRef.current = false;

    if (socketClientRef.current) {
      try {
        socketClientRef.current.removeAllListeners();
        socketClientRef.current.disconnect();
      } catch (_) {
        // best effort cleanup
      }
    }
    socketClientRef.current = null;
    socketConnectedRef.current = false;

    if (mqttClientRef.current?.end) {
      try {
        mqttClientRef.current.end(true);
      } catch (_) {
        // best effort cleanup
      }
    }
    mqttClientRef.current = null;
    mqttConnectedRef.current = false;
  }, [clearRealtimeStabilityTimer]);

  /**
   * Pin the app to polling and schedule the ONE allowed upgrade retry after the
   * cooldown. Called whenever realtime is unavailable or a confirmed link drops.
   * Repeated calls are idempotent — they never stack multiple retry timers.
   */
  const lockRealtimeToPolling = useCallback(() => {
    realtimeLockedRef.current = true;
    teardownRealtimeTransports();
    setActiveTransport('polling');
    startPollingFallback();

    if (realtimeRetryTimerRef.current) {
      return;
    }
    realtimeRetryTimerRef.current = globalThis.setTimeout(() => {
      realtimeRetryTimerRef.current = null;
      realtimeLockedRef.current = false;
      if (networkOnlineRef.current) {
        startRealtimeAttemptRef.current?.();
      } else {
        // Still offline at retry time — re-arm another cooldown; the NetInfo
        // listener will also kick an attempt the moment connectivity returns.
        lockRealtimeToPollingRef.current?.();
      }
    }, REALTIME_RETRY_COOLDOWN_MS);
  }, [REALTIME_RETRY_COOLDOWN_MS, startPollingFallback, teardownRealtimeTransports]);

  /**
   * A realtime transport reported a successful connection. We do NOT trust it
   * immediately — instead we keep showing polling and start (or keep) the
   * stability window. Only if the link is still up when the window elapses do we
   * confirm it and flip the badge to realtime.
   */
  const handleRealtimeConnected = useCallback(() => {
    if (realtimeLockedRef.current) {
      // An upgrade is not permitted right now; drop the stray connection.
      teardownRealtimeTransports();
      return;
    }
    if (realtimeConfirmedRef.current) {
      applyTransportState();
      return;
    }
    if (realtimeStabilityTimerRef.current) {
      return; // stability window already in progress
    }
    realtimeStabilityTimerRef.current = globalThis.setTimeout(() => {
      realtimeStabilityTimerRef.current = null;
      if (mqttConnectedRef.current || socketConnectedRef.current) {
        realtimeConfirmedRef.current = true;
        applyTransportState();
      } else {
        lockRealtimeToPollingRef.current?.();
      }
    }, REALTIME_STABILITY_MS);
  }, [REALTIME_STABILITY_MS, applyTransportState, teardownRealtimeTransports]);

  /**
   * A realtime transport dropped or failed to come up. If another transport is
   * still connected we just re-render. If we are merely offline this is a
   * network blip (not a realtime failure) so we wait for reconnection without
   * burning the cooldown. Otherwise realtime is genuinely unavailable → lock to
   * polling for the cooldown.
   */
  const handleRealtimeLost = useCallback(() => {
    if (mqttConnectedRef.current || socketConnectedRef.current) {
      applyTransportState();
      return;
    }
    if (!networkOnlineRef.current) {
      teardownRealtimeTransports();
      setActiveTransport('polling');
      startPollingFallback();
      return;
    }
    lockRealtimeToPollingRef.current?.();
  }, [applyTransportState, startPollingFallback, teardownRealtimeTransports]);

  const handleRealtimeOrderEvent = useCallback((payload = {}, meta = {}, eventType = 'modify') => {
    // Runs inside socket.io / MQTT callbacks: an exception here is uncaught and
    // kills the app in release builds, so one malformed order must never escape.
    try {
      if (!networkOnlineRef.current) {
        return;
      }

      const realtimeOrder = normalizeRealtimeOrder(payload);
      // Slim notices relayed from another API worker/host carry the id as
      // `documentId` (no `_id`, so older app builds reload instead of
      // inserting a half row).
      const slimNotice = isSlimRealtimeNotice(payload) || isSlimRealtimeNotice(realtimeOrder);
      const orderId = realtimeOrder?._id || payload?._id || payload?.orderId
        || (slimNotice ? (realtimeOrder?.documentId || payload?.documentId) : null);
      const eventId = payload?.__realtime?.eventId
        || realtimeOrder?.__realtime?.eventId
        || `${meta?.topic || eventType}:${orderId || 'unknown'}:${realtimeOrder?.updatedAt || realtimeOrder?.status || ''}`;

      if (isRealtimeDuplicate(eventId)) {
        return;
      }

      if (realtimeOrder && orderId) {
        const alreadyKnown = (ordersRef.current || []).some((order) => order && order._id === orderId);
        // A slim notice (relayed from another API worker/host) carries only
        // identity + status: patch an order we already show, never insert it as
        // a half row, and reload the full list shortly (bursts collapse).
        if (slimNotice) {
          if (alreadyKnown) {
            upsertOrderInMemory({ ...realtimeOrder, _id: orderId });
          }
          if (realtimeRefetchTimerRef.current) {
            globalThis.clearTimeout(realtimeRefetchTimerRef.current);
          }
          realtimeRefetchTimerRef.current = globalThis.setTimeout(() => {
            realtimeRefetchTimerRef.current = null;
            fetchOrdersFromAPIRef.current?.();
          }, REALTIME_REFETCH_DEBOUNCE_MS);
          return;
        }
        upsertOrderInMemory(realtimeOrder);
        // A brand-new order whose payload lacks the fields needed to place it in a
        // list (its place/type) would be inserted invisibly. Reconcile against the
        // API so the full order is fetched and shown — never silently swallowed.
        if (!alreadyKnown && !hasDisplayableOrderShape(realtimeOrder)) {
          fetchOrdersFromAPIRef.current?.();
        }
        return;
      }

      // If backend payload is partial, use API sync as a safe fallback.
      fetchOrdersFromAPIRef.current?.();
    } catch (error) {
      console.warn('[Orders] realtime event ignored (malformed payload):', error?.message);
      try {
        fetchOrdersFromAPIRef.current?.();
      } catch (_) {
        // best effort
      }
    }
  }, [REALTIME_REFETCH_DEBOUNCE_MS, isRealtimeDuplicate, normalizeRealtimeOrder, upsertOrderInMemory]);

  const handleSocketRealtimeEnvelope = useCallback((payload = {}, fallbackEvent = 'socket') => {
    const eventPayload = payload?.payload && typeof payload.payload === 'object'
      ? payload.payload
      : payload;
    const metadata = {
      topic: payload?.topic || payload?.event || fallbackEvent,
      transport: 'socket.io',
      receivedAt: new Date().toISOString(),
    };

    handleRealtimeOrderEvent(eventPayload, metadata, payload?.event || fallbackEvent);
  }, [handleRealtimeOrderEvent]);

  /**
   * Try a single Socket.IO candidate URL. We disable socket.io's own reconnect
   * loop (`reconnection: false`) because retries are driven by our cooldown
   * policy, not the client. On `connect_error`/`connect_timeout` we walk to the
   * next candidate; once every candidate has failed within this attempt we
   * report the loss, which engages the polling lock.
   */
  const connectSocketCandidate = useCallback(async (candidateIndex) => {
    if (realtimeLockedRef.current || !networkOnlineRef.current) {
      return;
    }
    if (candidateIndex >= SOCKET_IO_CANDIDATES.length) {
      handleRealtimeLostRef.current?.();
      return;
    }

    const socketUrl = SOCKET_IO_CANDIDATES[candidateIndex];
    const restaurantDataRaw = await AsyncStorage.getItem('restaurant');
    const restaurant = restaurantDataRaw ? JSON.parse(restaurantDataRaw) : null;
    const restaurantId = restaurant?._id;
    // The backend scopes all realtime traffic to restaurant rooms and
    // authorizes `join:restaurant` against the JWT's user (its `restaurants`
    // membership). An anonymous handshake gets `join:forbidden` and this
    // context degrades to polling, so send the login token like MailContext
    // does. Best-effort: a missing token just means an anonymous attempt.
    let authToken = null;
    try {
      authToken = await getAccessToken();
    } catch (_) {
      authToken = null;
    }

    let socket;
    try {
      socket = createSocketClient(socketUrl, {
        transports: ['websocket', 'polling'],
        timeout: SOCKET_CONNECT_TIMEOUT_MS,
        reconnection: false,
        auth: authToken ? { token: authToken } : undefined,
      });
    } catch (_) {
      connectSocketCandidateRef.current?.(candidateIndex + 1);
      return;
    }

    socketClientRef.current = socket;

    socket.on('connect', () => {
      socketConnectedRef.current = true;
      if (restaurantId) {
        socket.emit('join:restaurant', restaurantId);
      }
      handleRealtimeConnectedRef.current?.();
    });

    // The backend denied the room join (expired/absent token or a store this
    // user is not a member of). Without the room no order events will arrive,
    // so treat it as a lost realtime link — the anti-flap policy pins us to
    // polling, which needs no realtime authorization.
    socket.on('join:forbidden', () => {
      socketConnectedRef.current = false;
      handleRealtimeLostRef.current?.();
    });

    socket.on('disconnect', () => {
      socketConnectedRef.current = false;
      handleRealtimeLostRef.current?.();
    });

    socket.on('connect_error', () => {
      socketConnectedRef.current = false;
      // Abandon this candidate cleanly and move to the next one in-attempt.
      try {
        socket.removeAllListeners();
        socket.close();
      } catch (_) {
        // best effort cleanup
      }
      if (socketClientRef.current === socket) {
        socketClientRef.current = null;
      }
      connectSocketCandidateRef.current?.(candidateIndex + 1);
    });

    socket.on('order:create', (payload) => handleSocketRealtimeEnvelope(payload, 'order:create'));
    socket.on('order:modify', (payload) => handleSocketRealtimeEnvelope(payload, 'order:modify'));
    socket.on('order:pay', (payload) => handleSocketRealtimeEnvelope(payload, 'order:pay'));
    socket.on('order:cancel', (payload) => handleSocketRealtimeEnvelope(payload, 'order:cancel'));
    socket.on('realtime:event', (payload) => handleSocketRealtimeEnvelope(payload, payload?.event || 'realtime:event'));
  }, [SOCKET_CONNECT_TIMEOUT_MS, SOCKET_IO_CANDIDATES, handleSocketRealtimeEnvelope]);

  /**
   * Fetch orders from API with optimized caching
   */
  const fetchOrdersFromAPI = useCallback(async (options = {}) => {
    // A deliberate full sync (boot / foreground resume / opening the table map)
    // treats the server response as the COMPLETE set of today's orders, so a
    // stale local "open" order the server no longer returns — a ghost left after
    // its order was paid/closed/deleted on another device — is purged instead of
    // re-seeded. Routine 10s polls pass nothing, so a transiently-windowed poll
    // can still never drop a genuinely open order. Guard the shape so callers
    // that pass an unrelated event payload (Observables.call) don't opt in.
    const authoritative = !!(options && options.authoritative === true);
    // Race-free concurrency guard via a ref. The watchdog lets a fresh attempt
    // through if a previous sync has been "in progress" implausibly long, so a
    // hung request can never permanently wedge order refresh (the bug that used
    // to require an app restart to clear).
    if (!shouldAllowSync(isSyncingRef.current, syncStartedAtRef.current, Date.now(), SYNC_WATCHDOG_MS)) {
      // Queue, don't drop: the in-flight request may predate the change that
      // triggered this call (a realtime event, a just-sent order), so one more
      // fetch runs as soon as it settles. Repeated calls collapse into one run.
      queuedFetchOptionsRef.current = {
        authoritative: authoritative || !!queuedFetchOptionsRef.current?.authoritative,
      };
      return;
    }
    if (isSyncingRef.current) {
      ErrorReportingService.warn(
        'Order sync lock was stale — forcing a fresh fetch',
        { lockAgeMs: Date.now() - (syncStartedAtRef.current || 0) },
        'OrdersContext'
      );
    }

    isSyncingRef.current = true;
    const fetchStartedAt = Date.now();
    syncStartedAtRef.current = fetchStartedAt;
    emitApiToLocalStatus({ state: 'syncing', reason: null, reasonType: null });

    // Abort the request if the network silently stalls; without this a dropped
    // mobile connection never resolves fetch() and would hold the lock forever.
    const abortController = new AbortController();
    const timeoutId = globalThis.setTimeout(() => {
      try {
        abortController.abort();
      } catch (_) {
        // best effort
      }
    }, FETCH_TIMEOUT_MS);

    try {
      const token = await getAccessToken();
      const restaurantData = await AsyncStorage.getItem('restaurant');
      const restaurantPlaceData = await AsyncStorage.getItem('restaurantPlace');

      if (!token || !restaurantData || !restaurantPlaceData) {
        emitApiToLocalStatus({
          state: 'error',
          reasonType: 'auth',
          reason: 'Missing access token or restaurant context',
        });
        return;
      }

      const restaurant = JSON.parse(restaurantData);
      const place = JSON.parse(restaurantPlaceData);

      // Keep the realtime place-filter in sync with the active place (handles
      // place switches without needing a dedicated event).
      currentPlaceIdRef.current = place?._id ? place._id.toString() : null;

      // lowKey=true: the backend mirrors this context's own filterOrderProperties
      // whitelist server-side (media-stripped foods, identity-only user refs), so
      // the board poll ships only what this context keeps anyway — the full rows
      // weighed ~3.5 MB per poll on busy stores.
      const URL = `${API_URL}/restaurant/data/${restaurant._id}/orders?placeId=${place._id}&lowKey=true`;

      const response = await fetch(URL, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown');
        const apiError = new Error(`Server responded ${response.status}: ${errorText}`);
        apiError.httpStatus = response.status;
        throw apiError;
      }

      const data = await response.json();
      // The endpoint always answers with an array. Anything else (an error
      // envelope behind a 200, a proxy page) must fail this sync — treated as
      // an empty list it would look like every order vanished server-side.
      if (!Array.isArray(data)) {
        throw new Error('Order list response is not an array');
      }
      const filteredOrders = data.map((order) => filterOrderProperties(order));

      // Reconcile the windowed server response with what we already hold WITHOUT
      // losing any live order. The backend only returns today's orders (3am-3am)
      // for the current place, so a blind replace silently dropped still-open
      // orders and future pre-orders the window omitted — and, because the result
      // is cached, they stayed gone across a restart even though the order was
      // still in the database. reconcileOrderLists keeps the server authoritative
      // for everything it returns while preserving any live (open / in-progress /
      // pending_sync / scheduled) local order the narrow query missed; only
      // terminal orders age out. This is the core "never lose an order" guarantee.
      // Converge on the server without ever flickering a fresh order away:
      //  - a live local order missing from several consecutive responses is a
      //    confirmed ghost (closed / merged / moved / deleted on another device)
      //    and is purged even by a routine poll, so devices stop disagreeing;
      //  - orders touched on this device in the last few seconds are shielded;
      //  - orders changed locally after this request was sent keep the local
      //    copy (the snapshot is older) and a follow-up fetch is queued.
      const nowMs = Date.now();
      serverMissCountsRef.current = trackServerMisses(serverMissCountsRef.current, filteredOrders, ordersRef.current, nowMs);
      const protectedIds = collectRecentlyTouchedIds(localTouchedAtRef.current, nowMs);
      const preferLocalIds = collectTouchedSince(localTouchedAtRef.current, fetchStartedAt);
      if (preferLocalIds.size) {
        queuedFetchOptionsRef.current = {
          authoritative: !!queuedFetchOptionsRef.current?.authoritative,
        };
      }
      const mergedOrders = reconcileOrderLists(filteredOrders, ordersRef.current, new Date(nowMs), {
        authoritative,
        confirmedGhostIds: selectConfirmedGhostIds(serverMissCountsRef.current),
        protectedIds,
        preferLocalIds,
      })
        // Final safety net: drop any sibling-place order that slipped into the
        // local list before this fix (or during a place switch). The server
        // response is already place-scoped; this only prunes preserved-local
        // leaks so they age out instead of lingering across restarts.
        .filter((order) => orderBelongsToPlace(order, currentPlaceIdRef.current));

      // Commit (and cache) only if the reconciled list actually differs from
      // what we already hold; an unchanged poll is a no-op and triggers no
      // re-render. See commitOrders for the rationale.
      commitOrders(mergedOrders);
      lastSuccessfulFetchAtRef.current = Date.now();

      emitApiToLocalStatus({
        state: 'success',
        reason: null,
        reasonType: null,
        fetchedCount: data.length,
      });
    } catch (error) {
      const details = classifyApiToLocalError(error);
      emitApiToLocalStatus({
        state: 'error',
        reasonType: details.reasonType,
        reason: details.reason,
        httpStatus: details.httpStatus,
      });
      ErrorReportingService.warn('Failed to fetch orders', { error: error.message }, 'OrdersContext');
    } finally {
      globalThis.clearTimeout(timeoutId);
      // ALWAYS release the lock, on every path. This is the single most
      // important line for "orders stopped loading until restart".
      isSyncingRef.current = false;
      syncStartedAtRef.current = 0;
      const queuedOptions = queuedFetchOptionsRef.current;
      if (queuedOptions) {
        queuedFetchOptionsRef.current = null;
        globalThis.setTimeout(() => fetchOrdersFromAPIRef.current?.(queuedOptions), 0);
      }
    }
  }, [filterOrderProperties, commitOrders, classifyApiToLocalError, emitApiToLocalStatus]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Intentionally initialized once on mount; transports manage their own reconnects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOrdersFromAPIRef.current = fetchOrdersFromAPI;
  }, [fetchOrdersFromAPI]);

  /**
   * Get orders for a specific table - instant lookup
   */
  const getTableOrders = useCallback(
    (tableId) => {
      return tableOrders[tableId] || [];
    },
    [tableOrders]
  );

  /**
   * Update a single order with optimistic UI updates
   */
  const updateOrder = useCallback(
    (orderId, updates) => {
      // Optimistic update
      queueOrderUpdate(orderId, updates);

      // Optionally sync to API in background
      // This can be done asynchronously without blocking UI
    },
    [queueOrderUpdate]
  );

  /**
   * Optimistically flip an order's status and keep it flipped until the backend
   * echoes the change (see {@link applyStatusOverlay}).
   *
   * Callers persist the status via the API themselves; this only owns the
   * instant, snap-back-proof UI move. Without it, accepting a pending order does
   * not reach the accepted/right list until the NEXT successful refetch — which,
   * because the post-accept refetch is often swallowed by the in-flight-sync
   * guard, is effectively "when the next order is accepted". Registering the
   * overlay and re-committing the current base makes the row move at once and
   * survives every server-authoritative poll until the status is echoed.
   *
   * @param {string} orderId - id of the order whose status changed.
   * @param {string} status - the new (non-`pending`) status to show optimistically.
   * @returns {() => void} a revert function that drops the overlay and re-commits
   *   (call it if the backend persist fails so the row returns to its real status).
   */
  const setOrderStatusOptimistic = useCallback(
    (orderId, status) => {
      const id = orderId ? String(orderId) : '';
      if (!id || !status || status === 'pending') {
        return () => {};
      }

      pendingStatusRef.current.set(id, { orderId: id, status, savedAt: Date.now() });
      commitOrders(ordersRef.current || [], { persist: false });

      return () => {
        if (pendingStatusRef.current.delete(id)) {
          commitOrders(ordersRef.current || [], { persist: false });
        }
      };
    },
    [commitOrders]
  );

  /**
   * Free a dineIn table the moment it is fully paid on this device.
   *
   * The payment only reaches the backend through the offline queue, and nothing
   * used to touch the local tab after paying — the floor plan kept showing the
   * table busy until the queued POST synced AND a later refetch returned it
   * closed (and forever when the server never closed it). This overlays the
   * paid tab as `closed` (see {@link applyStatusOverlay}) until the server
   * echoes a terminal status or {@link PAYMENT_CLOSE_OVERLAY_TTL_MS} passes.
   * Registered as the `orders:closeOptimistic` observable for paymentHelper.
   *
   * @param {{orderId?: string, tableId?: string}} [payload] - paid order and its table.
   * @returns {boolean} true when at least one order was overlaid.
   */
  const closeOrderOptimistic = useCallback((payload = {}) => {
    const ids = selectPaidTableOrderIds(ordersRef.current || [], payload || {});
    if (!ids.length) {
      return false;
    }

    const savedAt = Date.now();
    ids.forEach((id) => {
      pendingStatusRef.current.set(id, {
        orderId: id,
        status: 'closed',
        savedAt,
        ttlMs: PAYMENT_CLOSE_OVERLAY_TTL_MS,
      });
    });
    commitOrders(ordersRef.current || [], { persist: false });

    return true;
  }, [commitOrders]);

  /**
   * Pair an online-order line to a local product OPTIMISTICALLY, then persist to
   * the backend in the background.
   *
   * The pairing is applied to the in-memory order immediately (so the operator
   * can keep working without waiting on the network) and re-applied on top of
   * every subsequent commit via {@link applyPendingPairings} until the backend
   * echoes it. On a failed save we drop the overlay — reverting the line to
   * "not paired" — and surface a premium alert so it can be retried. The backend
   * PUT is `food/pairing/{platform}/{remoteCode}/{sizeId}` (see
   * food.controller.js `updateFoodPairing`).
   *
   * @param {object} params
   * @param {object} params.order - the order being paired.
   * @param {object} params.pairingItem - the online-order line (item or extra).
   * @param {object} params.pairedFood - optimistic paired-food payload for the UI.
   * @param {string} params.sizeId - id of the chosen product size (foodSizeId).
   * @returns {Promise<{ ok: boolean, error?: Error }>}
   */
  const pairOrderItem = useCallback(
    async ({ order, pairingItem, pairedFood, sizeId } = {}) => {
      const orderId = order?._id ? String(order._id) : '';
      const itemId = pairingItem?._id ? String(pairingItem._id) : '';
      const remoteCode = pairingItem?.remoteCode;

      if (!orderId || !itemId || !pairedFood || !sizeId || !remoteCode || !order?.orderFrom) {
        HappyAlert.error(i18n.t('pairing_save_failed_title'), i18n.t('pairing_save_failed_message'));

        return { ok: false, error: new Error('Invalid pairing request') };
      }

      const key = `${orderId}:${itemId}`;
      // Register + apply the optimistic overlay right away (instant UI). Commit
      // the current list so the pairing shows even before the network responds.
      // foodId (the food's _id, what the backend writes to item.item) + sizeId let
      // applyPairingOverlay confirm/prune this pairing only once the SERVER echoes
      // this exact food — so re-pairing an already-paired line still gets full
      // revert protection. savedAt bounds the overlay's lifetime (TTL).
      pendingPairingsRef.current.set(key, {
        orderId,
        itemId,
        food: pairedFood,
        foodId: pairedFood?.foodId ? String(pairedFood.foodId) : String(sizeId),
        sizeId: String(sizeId),
        savedAt: Date.now(),
      });
      commitOrders(ordersRef.current || [], { persist: false });

      try {
        const token = await getAccessToken();
        const url = `${API_URL}/food/pairing/${order.orderFrom}/${encodeURIComponent(remoteCode)}/${sizeId}?platformId=${order?.venueId || ''}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Pairing save failed (${response.status})`);
        }

        // Pull the authoritative order; the overlay stays applied until the
        // server echoes the pairing, at which point applyPendingPairings prunes it.
        fetchOrdersFromAPIRef.current?.();

        return { ok: true };
      } catch (error) {
        // Revert: drop the overlay so the line returns to "not paired", re-commit
        // so the UI updates, then tell the operator so they can retry.
        pendingPairingsRef.current.delete(key);
        commitOrders(ordersRef.current || [], { persist: false });
        ErrorReportingService.warn('Background pairing save failed', { error: error?.message }, 'OrdersContext');
        HappyAlert.error(i18n.t('pairing_save_failed_title'), i18n.t('pairing_save_failed_message'));

        return { ok: false, error };
      }
    },
    [commitOrders]
  );

  /**
   * Store a new order and immediately reflect in state
   */
  const storeOrder = useCallback(
    async (orderData) => {
      try {
        const token = await getAccessToken();

        const response = await fetch(`${API_URL}/order/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData),
        });

        if (!response.ok) throw new Error('Failed to store order');

        const data = await response.json();
        const filteredOrder = filterOrderProperties(data);

        const nextOrders = [...(ordersRef.current || []), filteredOrder];
        commitOrders(nextOrders);

        return filteredOrder;
      } catch (error) {
        ErrorReportingService.error('Failed to store order', { error: error.message }, 'OrdersContext');
        throw error;
      }
    },
    [commitOrders, filterOrderProperties]
  );

  /**
   * Fetch orders by date range
   */
  const fetchOrdersByDate = useCallback(
    async (startDate, endDate) => {
      const startDay = new Date(startDate.setHours(0, 0, 1, 0));
      const endDay = new Date(endDate.setHours(23, 59, 59, 1));

      try {
        const token = await getAccessToken();
        const restaurantData = await AsyncStorage.getItem('restaurant');

        if (!token || !restaurantData) return;

        const URL = `${API_URL}/restaurant/data/orders?startDate=${startDay.toISOString()}&endDate=${endDay.toISOString()}`;

        const response = await fetch(URL, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) throw new Error('Failed to fetch orders by date');

        const data = await response.json();
        return data.map((order) => filterOrderProperties(order));
      } catch (error) {
        ErrorReportingService.warn(
          'Failed to fetch orders by date',
          { error: error.message },
          'OrdersContext'
        );
        throw error;
      }
    },
    [filterOrderProperties]
  );

  /**
   * Attempt to bring realtime up (MQTT preferred, Socket.IO in parallel as a
   * fallback). Suppressed while the polling lock is engaged, while offline, or
   * when an attempt/connection is already in flight. Failure of every transport
   * funnels through {@link handleRealtimeLost}, which engages the cooldown lock.
   */
  const startRealtimeAttempt = useCallback(() => {
    if (realtimeLockedRef.current || !networkOnlineRef.current) {
      return;
    }
    // An attempt or live connection already owns the transports.
    if (socketClientRef.current || mqttClientRef.current) {
      return;
    }

    realtimeConfirmedRef.current = false;
    clearRealtimeStabilityTimer();

    const mqttAvailable = typeof window !== 'undefined' && typeof window.startMQTTService === 'function';
    if (mqttAvailable) {
      const callbacks = {
        connect: () => {
          mqttConnectedRef.current = true;
          handleRealtimeConnectedRef.current?.();
        },
        disconnect: () => {
          mqttConnectedRef.current = false;
          handleRealtimeLostRef.current?.();
        },
        orderCreated: (message, meta) => handleRealtimeOrderEvent(message, meta, 'create'),
        modifyOrder: (message, meta) => handleRealtimeOrderEvent(message, meta, 'modify'),
        payOrder: (message, meta) => handleRealtimeOrderEvent(message, meta, 'pay'),
        cancelOrder: (message, meta) => handleRealtimeOrderEvent(message, meta, 'cancel'),
        reloadOrders: () => fetchOrdersFromAPI(),
      };

      try {
        mqttClientRef.current = window.startMQTTService(callbacks, {});
      } catch (error) {
        mqttClientRef.current = null;
        mqttConnectedRef.current = false;
        ErrorReportingService.warn('Failed to start MQTT transport', { error: error?.message }, 'OrdersContext');
      }
    } else {
      mqttConnectedRef.current = false;
    }

    // Socket.IO runs alongside MQTT; whichever confirms first keeps realtime up.
    connectSocketCandidateRef.current?.(0);
  }, [clearRealtimeStabilityTimer, fetchOrdersFromAPI, handleRealtimeOrderEvent]);

  // Wire the forward-referenced helpers so the mutually-recursive transport
  // functions can call the latest version through stable refs.
  useEffect(() => {
    startRealtimeAttemptRef.current = startRealtimeAttempt;
    lockRealtimeToPollingRef.current = lockRealtimeToPolling;
    connectSocketCandidateRef.current = connectSocketCandidate;
    handleRealtimeConnectedRef.current = handleRealtimeConnected;
    handleRealtimeLostRef.current = handleRealtimeLost;
  }, [startRealtimeAttempt, lockRealtimeToPolling, connectSocketCandidate, handleRealtimeConnected, handleRealtimeLost]);

  // Demo mode toggled → re-fetch so the currently displayed board immediately
  // reflects the new (redacted or real) data instead of waiting for the next poll.
  useEffect(() => subscribeDemoMode(() => {
    fetchOrdersFromAPI?.();
  }), [fetchOrdersFromAPI]);

  /**
   * Clean up and load cached orders on mount
   */
  // Intentionally run once on mount; reconnect/fallback logic is internal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let netInfoUnsubscribe;
    let appStateSubscription;

    const initialize = async () => {
      // Prime the active place id BEFORE any realtime event or cache seed so the
      // place filter is effective from the very first event (the first fetch
      // refreshes it again).
      try {
        const restaurantPlaceData = await AsyncStorage.getItem('restaurantPlace');
        const place = restaurantPlaceData ? JSON.parse(restaurantPlaceData) : null;
        currentPlaceIdRef.current = place?._id ? place._id.toString() : null;
      } catch (_) {
        currentPlaceIdRef.current = null;
      }

      // Load cached orders first
      try {
        const cachedOrders = await AsyncStorage.getItem('orders');
        if (cachedOrders) {
          const parsed = JSON.parse(cachedOrders);
          // Drop anything that no longer belongs to the current business day
          // BEFORE seeding, so a relaunch on a new day never flashes (or, while
          // offline, keeps showing) yesterday's cached orders. The first poll
          // reconciles against the same business-day bound. Without this the app
          // resurrected the previous day's orders on every launch.
          const fresh = pruneStaleCachedOrders(parsed)
            // Prune any sibling-place order that a pre-fix build cached.
            .filter((order) => orderBelongsToPlace(order, currentPlaceIdRef.current));
          // Seed via commitOrders so the signature is primed; the first poll
          // then no-ops if the server returns the same set we cached.
          commitOrders(fresh, { persist: false });
        }
      } catch (error) {
        console.warn('Failed to load cached orders:', error);
      }

      // Order transport — the boot fetch, the 10s polling fallback, the
      // Socket.IO/MQTT realtime link, and the NetInfo/AppState listeners — is
      // only needed by order-bearing modes (restaurant POS + manager). A service
      // technician or warehouse (stock) session must not pay a boot fetch, a
      // forever 10s poll, and socket/MQTT connection attempts it never uses. Keep
      // the provider mounted (context stays populated from the cache seed above so
      // no useOrders consumer breaks) but skip the network/transport machinery for
      // those modes. A mode switch triggers a full app restart, which re-runs this
      // effect under the new persisted mode — so no runtime "start on switch" path
      // is needed. Gated by EXCLUSION so any unknown/default mode keeps today's
      // full behavior; only the two explicitly non-order modes opt out.
      let userAppMode = 'restaurant';
      try {
        userAppMode = (await getSetting('userAppMode')) || 'restaurant';
      } catch (_) {
        userAppMode = 'restaurant';
      }
      if (userAppMode === 'service' || userAppMode === 'stock' || userAppMode === 'driver') {
        return;
      }

      // Fetch fresh data. Authoritative on boot: the first server response is a
      // complete view of today's orders, so any stale local "open" order it does
      // not return (a ghost left after the order was paid/closed/deleted on
      // another device) is purged from state AND the cache instead of resurfacing
      // on every launch. See reconcileOrderLists / isProvableServerGhost.
      fetchOrdersFromAPI({ authoritative: true });
      // Polling is always on until realtime is confirmed; the badge starts on
      // "Polling" and only flips once a link survives the stability window.
      startPollingFallback();
      startRealtimeAttemptRef.current?.();
      Observables.register('reloadOrders', fetchOrdersFromAPI);
      // Let the SyncManager collapse a device-minted optimistic order once the
      // backend has de-duplicated it into a different canonical order, so no
      // ghost "pending" order lingers on an already-occupied/closed table.
      Observables.register('orders:dropOptimistic', removeOptimisticOrder);
      // paymentHelper frees a fully paid table at once (before the queued
      // payment syncs) instead of leaving it busy on the floor plan.
      Observables.register('orders:closeOptimistic', closeOrderOptimistic);

      // Refresh the moment the app returns to the foreground. On both iOS and
      // Android the OS suspends JS timers (our 10s poll) while backgrounded, so
      // without this the user would stare at stale orders on resume until the
      // next tick fired — or, if the sync lock had wedged, never. We force an
      // immediate fetch and, if realtime is down and allowed, re-attempt it so
      // the socket reconnects right away instead of waiting out the cooldown.
      appStateSubscription = AppState.addEventListener('change', (nextAppState) => {
        const prev = appStateRef.current;
        appStateRef.current = nextAppState;
        if (!cameToForeground(prev, nextAppState)) {
          return;
        }
        if (!networkOnlineRef.current) {
          return;
        }
        // Pull the latest orders immediately so nothing is missed on resume.
        // Authoritative: a resume is a natural point to purge any table that was
        // paid/closed elsewhere while this device was backgrounded.
        fetchOrdersFromAPIRef.current?.({ authoritative: true });
        startPollingFallback();
        if (!realtimeLockedRef.current
          && !mqttConnectedRef.current && !socketConnectedRef.current) {
          startRealtimeAttemptRef.current?.();
        }
        applyTransportState();
      });

      netInfoUnsubscribe = NetInfo.addEventListener((state) => {
        const online = Boolean(state?.isConnected && state?.isInternetReachable !== false);
        const wasOnline = networkOnlineRef.current;
        networkOnlineRef.current = online;
        setIsConnected(online);

        if (!online) {
          // Network blip — keep polling, but don't burn the realtime cooldown.
          startPollingFallback();
          return;
        }

        // Back online: if realtime isn't currently up and we're not in the
        // cooldown lock, make a fresh upgrade attempt; otherwise keep polling
        // and let the scheduled retry handle the next upgrade.
        if (!wasOnline && !realtimeLockedRef.current
          && !mqttConnectedRef.current && !socketConnectedRef.current) {
          startRealtimeAttemptRef.current?.();
        }
        applyTransportState();
      });

      const initialNetState = await NetInfo.fetch();
      const isOnlineInitially = Boolean(initialNetState?.isConnected && initialNetState?.isInternetReachable !== false);
      networkOnlineRef.current = isOnlineInitially;
      setIsConnected(isOnlineInitially);
      applyTransportState();
    };

    initialize();

    return () => {
      if (realtimeRetryTimerRef.current) {
        globalThis.clearTimeout(realtimeRetryTimerRef.current);
        realtimeRetryTimerRef.current = null;
      }
      if (realtimeStabilityTimerRef.current) {
        globalThis.clearTimeout(realtimeStabilityTimerRef.current);
        realtimeStabilityTimerRef.current = null;
      }
      if (syncIntervalRef.current) {
        stopPollingFallback();
      }
      if (batchUpdatesRef.current) {
        globalThis.clearTimeout(batchUpdatesRef.current);
      }
      if (realtimeRefetchTimerRef.current) {
        globalThis.clearTimeout(realtimeRefetchTimerRef.current);
        realtimeRefetchTimerRef.current = null;
      }
      queuedFetchOptionsRef.current = null;
      if (netInfoUnsubscribe) {
        netInfoUnsubscribe();
      }
      if (appStateSubscription?.remove) {
        appStateSubscription.remove();
      }
      if (typeof Observables.remove === 'function') {
        Observables.remove('reloadOrders');
        Observables.remove('orders:dropOptimistic');
        Observables.remove('orders:closeOptimistic');
      }
      teardownRealtimeTransports();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({
      orders,
      tableOrders,
      isConnected,
      // Live read of the sync lock instead of React state: toggling state on
      // every poll/realtime refresh changed this context value twice per sync,
      // re-rendering every orders consumer app-wide even when no order changed
      // (which defeated the commitOrders signature gate). No screen renders it.
      get isSyncing() {
        return isSyncingRef.current;
      },
      activeTransport,
      fetchOrdersFromAPI,
      fetchOrdersByDate,
      storeOrder,
      updateOrder,
      // Optimistic online-order pairing: applies the pairing instantly and saves
      // it to the backend in the background (reverting + alerting on failure), so
      // the operator never waits on the PUT round-trip. See pairOrderItem.
      pairOrderItem,
      // Optimistic order-status change: moves an accepted/advanced order between
      // the pending and accepted lists INSTANTLY and keeps it there until the
      // backend echoes the status, so the online-orders screen no longer lags a
      // refetch behind (the "accepted order only shows on the next accept" bug).
      // Returns a revert fn to call if the caller's status PUT fails.
      setOrderStatusOptimistic,
      getTableOrders,
      queueOrderUpdate,
      // Optimistic insert/update used by Cart/Payment screens so a freshly
      // created table order shows up INSTANTLY (table colours as occupied)
      // without waiting for the backend round-trip. Reconciled later by the
      // socket `order:create` event and the periodic API sync.
      upsertOrder: upsertOrderInMemory,
    }),
    [orders, tableOrders, isConnected, activeTransport, fetchOrdersFromAPI, fetchOrdersByDate, storeOrder, updateOrder, pairOrderItem, setOrderStatusOptimistic, getTableOrders, queueOrderUpdate, upsertOrderInMemory]
  );

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
};

export default React.memo(OrdersProvider);

